import type { IUseCase } from '@/shared/contracts/use-case.contract';
import type { IAppDB } from '@/shared/lib/db';

import type { CartItem } from '@/shared/lib/db/schemas/cart.schema';
import { UIError } from '@/shared/utils/ui-error';
import { calculateAddonsPrice } from '../utils/calculate-addons-price';
import { validateAddonsLimit } from '../utils/validate-addons-limit';
import { calculateComboChoicesPrice } from '@/modules/stores/utils/combo';

export type UseCaseInput = {
  id: string;
  data: Partial<Omit<CartItem, 'id' | 'total_price'>>;
};
export type UseCaseOutput = void;

export class EditItemUseCase implements IUseCase<UseCaseInput, UseCaseOutput> {
  private db: IAppDB;

  constructor(db: IAppDB) {
    this.db = db;
  }

  async execute(input: UseCaseInput): Promise<UseCaseOutput> {
    const { id, data } = input;

    // Busca o item existente para manter os campos não fornecidos
    const existingItem = await this.db.items.get(id);
    if (!existingItem) {
      throw new Error('Item não encontrado');
    }

    // Aplica o limite por complemento (issue 311) sobre os adicionais efetivos
    // (os novos, se enviados; senão os já persistidos) antes de recalcular preço.
    const effectiveAddons = data.addons ?? existingItem.addons;
    const addonsLimit = validateAddonsLimit(effectiveAddons);
    if (!addonsLimit.isValid) {
      throw new UIError('ADDON_LIMIT_EXCEEDED', addonsLimit.message);
    }

    // Calcula o preço total automaticamente considerando consider_most_expensive
    const addonsTotalPrice = calculateAddonsPrice(effectiveAddons);

    // Combo configurável (PRAT-20): as escolhas (novas ou já gravadas) somam ao
    // preço por unidade.
    const comboChoicesPrice = calculateComboChoicesPrice(
      data.combo_choices ?? existingItem.combo_choices
    );

    const totalPrice =
      ((data.price ?? existingItem.price) + addonsTotalPrice + comboChoicesPrice) *
      (data.quantity ?? existingItem.quantity);

    // Atualiza apenas os campos fornecidos, mantendo os existentes
    const updateData: Partial<CartItem> = {
      total_price: totalPrice,
    };

    // Só atualiza os campos que foram fornecidos
    if (data.observation !== undefined) updateData.observation = data.observation;
    if (data.quantity !== undefined) updateData.quantity = data.quantity;
    if (data.item_id !== undefined) updateData.item_id = data.item_id;
    if (data.name !== undefined) updateData.name = data.name;
    if (data.price !== undefined) updateData.price = data.price;
    if (data.image_id !== undefined) updateData.image_id = data.image_id;
    if (data.addons !== undefined) updateData.addons = data.addons;
    if (data.store_slug !== undefined) updateData.store_slug = data.store_slug;
    if (data.combo_choices !== undefined) updateData.combo_choices = data.combo_choices;
    if (data.category_id !== undefined) updateData.category_id = data.category_id;

    await this.db.items.update(id, updateData);
  }
}
